

































































import { computed, defineComponent, PropType, ref } from "@vue/composition-api";
import { ExternalVerificationStatus } from "@scrile/api-provider/dist/api/OlecamsProvider";
import { UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { QuickTip } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseIcon from "@/components/BaseIcon";
import BaseSpinner from "@/components/BaseSpinner";
import PageTitle from "@/components/PageTitle";
import QuickTipsItem from "@/components/QuickTipsItem";

export default defineComponent({
  name: "ChoosePostType",
  components: { BaseSpinner, QuickTipsItem, PageTitle, BaseButton, BaseIcon, BaseConfirm },
  props: {
    loading: { type: Boolean, default: true },
    showChooseType: { type: Boolean, required: true },
    userId: { type: String, required: true },
    onlyLivesAllowed: { type: Boolean, default: false },
    externalVerificationStatus: {
      type: String as PropType<ExternalVerificationStatus>,
      default: ExternalVerificationStatus.PENDING,
    },
  },
  setup(props, { emit }) {
    const { authUser, authorities, isCreator, userVerificationStatus } = useAuthUser();

    const processingType = ref<ContentFeedTypeEnum | null>(null);

    const isVerifiedCreator = computed(
      () => isCreator.value && userVerificationStatus.value === UserVerificationStatus.VERIFIED
    );

    const isAuthor = computed(() => authUser.value?.id === props.userId);

    const canCreateEvent = computed(() =>
      checkAuthorities(
        authorities.value,
        isAuthor.value ? ["updateMyEvent", "getMyEvent"] : ["updateAnyEvent", "getAnyEvent"]
      )
    );

    const featureEvents = computed(() => config.featureEvents && canCreateEvent.value);

    const showLiveAlert = computed(
      () => props.externalVerificationStatus === ExternalVerificationStatus.PASSWORD_EXPIRED
    );

    const tipItem = computed<QuickTip>(() => ({
      icon: "sci-information-triangle",
      type: "warning",
      text: $t("Password expired, you must reset your password."),
    }));

    const disableGoLive = computed(() => props.externalVerificationStatus !== ExternalVerificationStatus.ACTIVE);

    const allowStreaming = computed(
      () =>
        isAuthor.value &&
        checkAuthorities(authorities.value, ["havePublicLivechat"]) &&
        config.featureGoLive &&
        isVerifiedCreator.value
    );

    const onlyPostsAllowed = computed(
      () => !featureEvents.value && !config.featureStories && !allowStreaming.value && !config.featureTextPosts
    );

    const selectType = (type: ContentFeedTypeEnum) => {
      if (processingType.value) {
        return;
      }
      processingType.value = type;
      emit("selectType", type, () => (processingType.value = null));
    };

    if (onlyPostsAllowed.value) {
      processingType.value = ContentFeedTypeEnum.MEDIA;
      emit("selectType", ContentFeedTypeEnum.MEDIA, () => (processingType.value = null));
    }

    return {
      authUser,
      authorities,
      isVerifiedCreator,
      ContentFeedTypeEnum,
      processingType,
      isAuthor,
      canCreateEvent,
      config,
      featureEvents,
      allowStreaming,
      disableGoLive,
      showLiveAlert,
      onlyPostsAllowed,
      tipItem,
      selectType,
    };
  },
});
